import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Palette, Plus, CheckCircle2, RotateCcw, Trash2, AlertCircle, Clock, Loader2,
  Image as ImageIcon, Paperclip,
} from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import ImageLightbox from './DesignImageLightbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TYPES = ['CAM Piece', 'Mould', 'CAD', 'Render', 'Other'];
const MAX_FILES = 10;
const MAX_BYTES = 5 * 1024 * 1024;

const imgSrc = (depId, img) => {
  if (img.url) return img.url;
  const token = localStorage.getItem('siddhi_token') || '';
  return `${API}/design-dependencies/${depId}/images/${img.id}?token=${encodeURIComponent(token)}`;
};

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, color: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-500/15', border: 'border-orange-300/60 dark:border-orange-500/30' },
  overdue: { label: 'Overdue', icon: AlertCircle, color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-100 dark:bg-rose-500/15', border: 'border-rose-300/60 dark:border-rose-500/30' },
  completed_on_time: { label: 'Completed On Time', icon: CheckCircle2, color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-100 dark:bg-emerald-500/15', border: 'border-emerald-300/60 dark:border-emerald-500/30' },
  completed_late: { label: 'Completed Late', icon: CheckCircle2, color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-500/15', border: 'border-amber-300/60 dark:border-amber-500/30' },
};

const isCompleted = (s) => s === 'completed_on_time' || s === 'completed_late';

const ThumbStrip = ({ depId, images, onOpen, label }) => {
  if (!images || images.length === 0) return null;
  const shown = images.slice(0, 3);
  const rest = Math.max(0, images.length - shown.length);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`thumb-strip-${label}`}>
      {shown.map((img, i) => (
        <button
          type="button"
          key={img.id}
          onClick={() => onOpen(i)}
          className="h-12 w-12 rounded-md overflow-hidden bg-muted/40 border border-border hover:ring-2 hover:ring-violet-400 transition-all"
          title={img.name}
          data-testid={`thumb-${label}-${img.id}`}
        >
          <img
            src={imgSrc(depId, img)}
            alt={img.name}
            className="h-full w-full object-cover"
          />
        </button>
      ))}
      {rest > 0 && (
        <button
          type="button"
          onClick={() => onOpen(shown.length)}
          className="h-12 w-12 rounded-md bg-muted/40 border border-border text-[11px] font-bold flex items-center justify-center hover:bg-muted"
          data-testid={`thumb-more-${label}`}
        >
          +{rest}
        </button>
      )}
    </div>
  );
};

const validateFiles = (existingCount, files) => {
  const list = Array.from(files || []);
  if (list.length === 0) return [];
  if (existingCount + list.length > MAX_FILES) {
    toast.error(`Max ${MAX_FILES} images allowed`);
    return [];
  }
  for (const f of list) {
    if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); return []; }
    if (f.size > MAX_BYTES) { toast.error(`${f.name}: max 5 MB`); return []; }
  }
  return list;
};

const uploadAll = async (url, files, headers) => {
  for (const f of files) {
    const fd = new FormData();
    fd.append('file', f);
    await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data', ...(headers || {}) } });
  }
};

const DepRow = ({ dep, onComplete, onReopen, onDelete, onAddImage, onDeleteImage, busyId }) => {
  const meta = STATUS_META[dep.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const completed = isCompleted(dep.status);
  const reqRef = useRef(null);
  const replyRef = useRef(null);
  const [lightbox, setLightbox] = useState({ side: null, idx: null });
  const [moulds, setMoulds] = useState([]);

  const allImages = useMemo(() => ({
    request: dep.request_images || [],
    reply: dep.reply_images || [],
  }), [dep]);

  // Auto-fetch moulds linked to this style (style page integration)
  useEffect(() => {
    if (!dep.style_number) { setMoulds([]); return; }
    axios.get(`${API}/moulds/by-style/${encodeURIComponent(dep.style_number)}`)
      .then(r => setMoulds(r.data.moulds || []))
      .catch(() => setMoulds([]));
  }, [dep.style_number]);

  return (
    <div
      className={cn('rounded-lg border p-2.5 space-y-2', meta.bg, meta.border)}
      data-testid={`dep-row-${dep.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-bold bg-background/60">
              {dep.dependency_type}
            </Badge>
            <span className="text-xs font-mono font-semibold truncate">{dep.style_number}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {dep.assigned_to_name || 'Unassigned'} · Due {formatDate(dep.due_date)}
          </p>
        </div>
        <div className={cn('flex items-center gap-1 text-[10px] font-bold flex-shrink-0', meta.color)}>
          <Icon className="h-3 w-3" />
          {meta.label}
        </div>
      </div>

      {dep.request_notes && (
        <p className="text-[11px] text-foreground italic bg-background/40 px-2 py-1 rounded">
          “{dep.request_notes}”
          {dep.created_by_name ? <span className="text-muted-foreground"> — {dep.created_by_name}</span> : null}
        </p>
      )}
      {allImages.request.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">References ({allImages.request.length})</p>
          <ThumbStrip
            depId={dep.id}
            images={allImages.request}
            label="request"
            onOpen={(i) => setLightbox({ side: 'request', idx: i })}
          />
        </div>
      )}
      {moulds.length > 0 && (
        <div data-testid={`dep-moulds-${dep.id}`}>
          <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Required Moulds ({moulds.length})</p>
          <div className="space-y-0.5 mt-0.5">
            {moulds.map(m => (
              <div key={m.id} className="flex items-center gap-1.5 text-[11px]">
                <span className="font-mono font-semibold">{m.mould_number}</span>
                <span className="text-muted-foreground">P{m.part_number}/{m.total_parts}</span>
                <Badge
                  className={cn(
                    'gap-1 font-semibold border h-4 px-1.5 text-[9px]',
                    m.current_location === 'Siddhi Jewels'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60'
                      : 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border-orange-300/60'
                  )}
                >
                  {m.current_location}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      {(dep.reply_notes || allImages.reply.length > 0) && (
        <div className="border-t border-border/60 pt-2 space-y-1">
          {dep.reply_notes && (
            <p className="text-[11px] text-emerald-900 dark:text-emerald-100 italic">
              ↪ “{dep.reply_notes}”
            </p>
          )}
          {allImages.reply.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Designer Reply ({allImages.reply.length})</p>
              <ThumbStrip
                depId={dep.id}
                images={allImages.reply}
                label="reply"
                onOpen={(i) => setLightbox({ side: 'reply', idx: i })}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-1.5">
        {!completed ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px] flex-1 bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 border-emerald-300/60 text-emerald-800 dark:text-emerald-200"
              disabled={busyId === dep.id}
              onClick={() => onComplete(dep.id, replyRef.current?.files)}
              data-testid={`dep-complete-${dep.id}`}
            >
              {busyId === dep.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Mark Complete</>}
            </Button>
            <input
              ref={replyRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const list = validateFiles(allImages.reply.length, e.target.files);
                if (list.length === 0) { e.target.value = ''; return; }
                onAddImage(dep.id, 'reply', list).then(() => { e.target.value = ''; });
              }}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              onClick={() => replyRef.current?.click()}
              title="Attach reply images"
              data-testid={`dep-attach-reply-${dep.id}`}
            >
              <Paperclip className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px] flex-1"
            disabled={busyId === dep.id}
            onClick={() => onReopen(dep.id)}
            data-testid={`dep-reopen-${dep.id}`}
          >
            {busyId === dep.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" /> Reopen</>}
          </Button>
        )}
        <input
          ref={reqRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const list = validateFiles(allImages.request.length, e.target.files);
            if (list.length === 0) { e.target.value = ''; return; }
            onAddImage(dep.id, 'request', list).then(() => { e.target.value = ''; });
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={() => reqRef.current?.click()}
          title="Add reference images"
          data-testid={`dep-attach-request-${dep.id}`}
        >
          <ImageIcon className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
          onClick={() => onDelete(dep.id)}
          data-testid={`dep-delete-${dep.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {completed && dep.completed_by_name && (
        <p className="text-[10px] text-muted-foreground italic">
          ✓ {dep.completed_by_name} · {formatDate(dep.completed_date)}
        </p>
      )}

      {lightbox.idx != null && (
        <ImageLightbox
          depId={dep.id}
          images={allImages[lightbox.side]}
          openIndex={lightbox.idx}
          onClose={() => setLightbox({ side: null, idx: null })}
        />
      )}
    </div>
  );
};

export const AddDepDialog = ({ orderId, orderHint, designers, open, onOpenChange, onAdded }) => {
  const [type, setType] = useState('CAD');
  const [styleNumber, setStyleNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]); // File[]
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setType('CAD'); setStyleNumber(''); setDueDate(''); setAssignedTo('');
    setNotes(''); setFiles([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!orderId) return toast.error('Order is required');
    if (!styleNumber.trim()) return toast.error('Style number is required');
    if (!dueDate) return toast.error('Due date is required');
    if (!assignedTo) return toast.error('Please assign to a designer');
    setSaving(true);
    try {
      const { data: created } = await axios.post(`${API}/design-dependencies`, {
        order_id: orderId,
        dependency_type: type,
        style_number: styleNumber.trim(),
        due_date: dueDate,
        assigned_to_user_id: assignedTo,
        request_notes: notes.trim() || null,
      });
      if (files.length > 0) {
        await uploadAll(`${API}/design-dependencies/${created.id}/images/request`, files);
      }
      toast.success('Request created');
      reset();
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-dep-dialog">
        <DialogHeader>
          <DialogTitle>Add Design Request</DialogTitle>
          <DialogDescription>
            {orderHint ? `Track a design task for ${orderHint}.` : 'Track a design task for this order.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="dep-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Style Number</Label>
            <Input
              value={styleNumber}
              onChange={(e) => setStyleNumber(e.target.value)}
              placeholder="e.g. ST-1050"
              data-testid="dep-style-input"
            />
          </div>
          <div>
            <Label className="text-xs">Due Date</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="dep-due-input"
            />
          </div>
          <div>
            <Label className="text-xs">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger data-testid="dep-assignee-select">
                <SelectValue placeholder={designers.length ? 'Pick a designer' : 'No designers found'} />
              </SelectTrigger>
              <SelectContent>
                {designers.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.full_name || d.name || d.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {designers.length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">
                No users with role "designer" exist yet. Create one from Users → Add user.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Request Notes (what's needed)</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Need 3 angle renders, gold tone, plain background"
              data-testid="dep-notes-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reference Images (optional, up to 10)</Label>
            <Input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => {
                const list = validateFiles(0, e.target.files);
                setFiles(list);
              }}
              data-testid="dep-files-input"
            />
            {files.length > 0 && <p className="text-[10px] text-muted-foreground mt-1">{files.length} file(s) selected</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="dep-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DesignDependenciesCard = ({ orderId, orderHint, onStateChange }) => {
  const [data, setData] = useState({ dependencies: [], summary: { total: 0, completed: 0, pending: 0, overdue: 0 }, order_state: 'no_dependencies' });
  const [loading, setLoading] = useState(true);
  const [designers, setDesigners] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/orders/${orderId}/design-dependencies`);
      setData(r.data);
      if (onStateChange) onStateChange(r.data.order_state);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [orderId, onStateChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/users`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.users || []);
      setDesigners(list.filter(u => u.role === 'designer' && u.is_active !== false));
    }).catch(() => setDesigners([]));
  }, []);

  const handleComplete = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/design-dependencies/${id}/complete`, {});
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark complete');
    } finally { setBusyId(null); }
  };
  const handleReopen = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/design-dependencies/${id}/reopen`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reopen');
    } finally { setBusyId(null); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dependency?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/design-dependencies/${id}`);
      await load();
      toast.success('Deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally { setBusyId(null); }
  };
  const handleAddImage = async (depId, side, files) => {
    setBusyId(depId);
    try {
      await uploadAll(`${API}/design-dependencies/${depId}/images/${side}`, files);
      await load();
      toast.success(`Added ${files.length} image(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusyId(null); }
  };

  const pct = data.summary.total > 0 ? Math.round((data.summary.completed / data.summary.total) * 100) : 0;
  const stateBadge = useMemo(() => {
    if (data.order_state === 'ready_for_production') {
      return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 hover:bg-emerald-100 border-emerald-300/60">Ready for Production</Badge>;
    }
    if (data.order_state === 'waiting_on_design') {
      return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300 hover:bg-rose-100 border-rose-300/60">Waiting on Design</Badge>;
    }
    return null;
  }, [data.order_state]);

  return (
    <Card data-testid="design-dependencies-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Palette className="h-4 w-4 text-violet-500" /> Design Dependencies
          </CardTitle>
          {stateBadge}
        </div>
        <div className="grid grid-cols-4 gap-1.5 mt-2 text-center">
          <div className="rounded-md bg-muted/50 py-1.5" data-testid="dep-stat-total">
            <p className="text-base font-bold tabular-nums leading-none">{data.summary.total}</p>
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</p>
          </div>
          <div className="rounded-md bg-emerald-50 dark:bg-emerald-500/10 py-1.5" data-testid="dep-stat-completed">
            <p className="text-base font-bold tabular-nums leading-none text-emerald-700 dark:text-emerald-300">{data.summary.completed}</p>
            <p className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300/80">Done</p>
          </div>
          <div className="rounded-md bg-orange-50 dark:bg-orange-500/10 py-1.5" data-testid="dep-stat-pending">
            <p className="text-base font-bold tabular-nums leading-none text-orange-700 dark:text-orange-300">{data.summary.pending}</p>
            <p className="text-[9px] uppercase tracking-wider text-orange-700 dark:text-orange-300/80">Pending</p>
          </div>
          <div className="rounded-md bg-rose-50 dark:bg-rose-500/10 py-1.5" data-testid="dep-stat-overdue">
            <p className="text-base font-bold tabular-nums leading-none text-rose-700 dark:text-rose-300">{data.summary.overdue}</p>
            <p className="text-[9px] uppercase tracking-wider text-rose-700 dark:text-rose-300/80">Overdue</p>
          </div>
        </div>
        {data.summary.total > 0 && <Progress value={pct} className="h-1.5 mt-2" />}
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-4">Loading…</p>
        ) : data.dependencies.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">No design dependencies yet.</p>
        ) : (
          <ScrollArea className="max-h-[420px] pr-2">
            <div className="space-y-2">
              {data.dependencies.map(dep => (
                <DepRow
                  key={dep.id}
                  dep={dep}
                  onComplete={handleComplete}
                  onReopen={handleReopen}
                  onDelete={handleDelete}
                  onAddImage={handleAddImage}
                  busyId={busyId}
                />
              ))}
            </div>
          </ScrollArea>
        )}
        <Button
          size="sm"
          className="w-full mt-2"
          onClick={() => setOpen(true)}
          data-testid="add-dep-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Request
        </Button>
        <AddDepDialog
          orderId={orderId}
          orderHint={orderHint}
          designers={designers}
          open={open}
          onOpenChange={setOpen}
          onAdded={load}
        />
      </CardContent>
    </Card>
  );
};

export default DesignDependenciesCard;
